import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ProfileDetail } from "@/interface";
import { getInitials } from "@/utils";
import { Avatar } from "@/components/ui/avatar";
import { CandidateActionsDropdown } from "../common/CandidateActionsDropdown";

export function EditorHeader({
  profile,
  profileBackUrl,
}: {
  profile: ProfileDetail;
  profileBackUrl: string;
}) {
  const fullName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    profile.username
    : "Candidate Profile";

  return (
    <div className="bg-[#1a0b2e] rounded-2xl overflow-hidden relative border border-purple-500/20 shadow-xl">
      <div className="absolute inset-0 bg-[url('/candidate-bg.jpg')] bg-cover bg-center opacity-40 mix-blend-screen" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a0b2e] via-[#1a0b2e]/90 to-transparent" />

      <div className="relative p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <Avatar
              src={profile?.profile_picture}
              name={fullName}
              className="h-20 w-20 rounded-2xl border-4 border-[#1a0b2e] shadow-lg"
              fallbackClassName="text-2xl"
            />
            {/* <div className="absolute -bottom-2 -right-2 h-5 w-5 bg-emerald-500 border-2 border-[#1a0b2e] rounded-full" /> */}
          </div>

          <div className="text-white space-y-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {fullName}{" "}
              {/* <CheckCircle2 className="h-4 w-4 text-amber-500 fill-amber-500" /> */}
            </h1>
            <div className="flex items-center gap-4 text-purple-200 text-sm">
              <span>
                {profile?.employee_code ||
                  profile?.employee_id ||
                  `EMP-${profile?.id || "001"}`}
              </span>
              <span className="text-slate-400">|</span>
              <span>{profile?.job_role || "Candidate"}</span>
              <span className="text-slate-400">|</span>
              <span>
                {profile?.department_detail?.department_name ||
                  "Department N/A"}
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs text-purple-300 pt-1">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />{" "}
                {profile?.work_location || profile?.city || "Location N/A"}
              </span>
              {profile?.joining_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Joined:{" "}
                  {profile.joining_date}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 self-start lg:self-auto">
          <Link
            to={profileBackUrl}
            className="text-purple-300 hover:text-white text-xs font-medium flex items-center transition-colors"
          >
            <ChevronLeft className="h-3 w-3 mr-1" /> Back to Profile
          </Link>
          <CandidateActionsDropdown profile={profile} />
        </div>
      </div>
    </div>
  );
}
